import React, { useState } from "react";
import PropTypes from "prop-types";
import AddLanguageModal from "components/Common/AddLanguageModal";

const TemplateTableView = ({ 
  templates, 
  loading, 
  error, 
  searchQuery, 
  handleSearchInputChange, 
  handlePageChange, 
  handleDelete, 
  currentPage, 
  totalPages, 
  formatDate, 
  perPage, 
  deletingId,
  onLanguageAdded
}) => { 
  const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const handleAddLanguageClick = (templateId) => {
    setSelectedTemplateId(templateId);
    setShowAddLanguageModal(true);
  };

  const handleModalClose = () => {
    setShowAddLanguageModal(false);
    setSelectedTemplateId(null);
  };

  const renderPagination = () => 
    totalPages > 1 && ( 
      <div className="d-flex justify-content-center align-items-center my-4"> 
        <button 
          className="btn btn-outline-primary me-2" 
          onClick={() => handlePageChange(Math.max(currentPage - 1, 1))} 
          disabled={currentPage === 1} 
        > 
          Previous 
        </button> 
        {Array.from({ length: totalPages }, (_, index) => ( 
          <button 
            key={index} 
            className={`btn mx-1 ${currentPage === index + 1 ? "btn-primary" : "btn-outline-secondary"}`} 
            onClick={() => handlePageChange(index + 1)} 
          > 
            {index + 1} 
          </button> 
        ))} 
        <button 
          className="btn btn-outline-primary ms-2" 
          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))} 
          disabled={currentPage === totalPages} 
        > 
          Next 
        </button> 
      </div> 
    ); 

  return ( 
    <div className="container bg-white rounded shadow-sm position-relative"> 
      {error && <div className="alert alert-danger text-center my-4">{error}</div>} 
      <div className="card-header d-flex justify-content-between align-items-center bg-white border-0 py-3 px-4"> 
        <div className="d-flex ms-auto" role="search"> 
          <input 
            className="form-control me-2" 
            type="search" 
            placeholder="Search templates" 
            aria-label="Search" 
            value={searchQuery} 
            onChange={handleSearchInputChange} 
          /> 
        </div> 
      </div> 
      <div className="card-body p-0"> 
        <div className="table-responsive"> 
          <table className="table table-hover mb-0"> 
            <thead className="bg-light"> 
              <tr> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start text-nowrap">Sr. No</th> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start text-nowrap">Template Name</th> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start">Type</th> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start">Languages</th> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start text-nowrap">Created On</th> 
                <th className="text-secondary text-uppercase py-3 px-4 small text-start">Actions</th> 
              </tr> 
            </thead> 
            <tbody> 
              {templates.length > 0 ? ( 
                templates.map((template,index) => ( 
                  <tr key={template.id}> 
                    <td className="align-middle px-4 py-3 text-start text-nowrap">{(currentPage - 1) * perPage + index + 1}</td> 
                    <td className="align-middle px-4 py-3 text-start">{template.name}</td> 
                    <td className="align-middle px-4 py-3 text-start"> 
                      <span 
                        className={`badge rounded-pill ${template.type === "EMAIL" ? "bg-primary" : "bg-success"} text-white text-nowrap`} 
                      > 
                        {template.type} 
                      </span> 
                    </td> 
                    <td className="align-middle px-4 py-3 text-start"> 
                      <div className="d-flex flex-wrap gap-2"> 
                        {template.languages.map((lang, index) => ( 
                          <span 
                            key={index} 
                            className="badge bg-light text-secondary border border-secondary px-2 py-1" 
                          > 
                            {lang} 
                          </span> 
                        ))}
                      </div>
                    </td>
                    <td className="align-middle px-4 py-3 text-start text-nowrap">{formatDate(template.created_at)}</td>
                    <td className="align-middle px-4 py-3 text-start d-flex gap-2">
                      <button 
                        onClick={() => handleDelete(template.id)}
                        className="btn btn-danger btn-sm"
                        disabled={deletingId === template.id}
                      >
                        {deletingId === template.id ? "Deleting..." : "Delete"}
                      </button>
                      <button
                        onClick={() => handleAddLanguageClick(template.id)}
                        className="btn btn-success btn-sm"
                      >
                        Add Language
                      </button>
                    </td>
                  </tr>
                )) 
              ) : ( 
                <tr> 
                  <td colSpan="6" className="text-center text-muted py-4">No templates found.</td> 
                </tr> 
              )} 
            </tbody> 
          </table> 
        </div> 
      </div> 
      {renderPagination()} 
      {showAddLanguageModal && (
        <AddLanguageModal
          show={showAddLanguageModal}
          onClose={handleModalClose}
          onSuccess={onLanguageAdded}
          templateId={selectedTemplateId}
        />
      )}
    </div> 
  ); 
}; 

TemplateTableView.propTypes = { 
  templates: PropTypes.array.isRequired, 
  loading: PropTypes.bool.isRequired, 
  error: PropTypes.string, 
  searchQuery: PropTypes.string.isRequired, 
  handleSearchInputChange: PropTypes.func.isRequired, 
  handlePageChange: PropTypes.func.isRequired, 
  handleDelete: PropTypes.func.isRequired, 
  currentPage: PropTypes.number.isRequired, 
  totalPages: PropTypes.number.isRequired, 
  formatDate: PropTypes.func.isRequired, 
  perPage: PropTypes.number, 
  deletingId: PropTypes.number,
  onLanguageAdded: PropTypes.func.isRequired
}; 

export default TemplateTableView;